import React, { createContext, useEffect, useMemo, useRef, useState } from "react";

import { useQueries } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { useAppState } from "../../../../../../context/StateContext";
import { Agent } from "../../../../../../models/agent";
import { Commodity } from "../../../../../../models/commodity";
import { ContainerSize } from "../../../../../../models/container.size";
import { Document } from "../../../../../../models/document";
import { Job } from "../../../../../../models/job";
import { Milestone } from "../../../../../../models/milestone";
import { Order } from "../../../../../../models/order";
import { Waybill } from "../../../../../../models/waybill";
import AgentService from "../../../../../../services/agent.service";
import DocumentService from "../../../../../../services/document.service";
import WaybillsService from "../../../../../../services/waybill.service";
import { Status } from "../../../../../../util/enums";

type Request = Array<
  [setState: Function, execute: Function, ...executeArgs: any, enabled?: boolean]
>;

interface ShipmentContext {
  shipment?: Waybill;
  agents: Agent[];
  documents: Document[];
  commodities: Commodity[];
  containerSizes: ContainerSize[];
  milestones: Milestone[];
  isLoading?: boolean;
  hasUnsavedChanges?: boolean;
  minContainerSequence?: number;
  syncReferenceWithOrder?: boolean;
  setShipment: (waybill: Waybill, noChanges?: boolean) => void;
  setHasUnsavedChanges: (value: boolean) => void;
}

const ShipmentsContext = createContext<ShipmentContext>({} as ShipmentContext);

const defaultNewShipment = (): Waybill =>
  ({
    shipmentType: "Sea",
    containers: [{}],
  }) as Waybill;

function buildJobFromOrder(order: Order): Job {
  return {
    id: order.id ?? 0,
    reference: order.reference,
    customer: order.customer,
    moveCoordinator: order.moveCoordinator,
    manager: order.manager,
    bookingAgent: order.bookingAgent,
    user: order.user,
  } as Job;
}

export function shouldSyncShipmentReferenceWithOrder(
  shipmentReference?: string,
  previousSyncedOrderReference?: string
) {
  const currentReference = shipmentReference?.trim();

  if (!currentReference) return true;

  return currentReference === previousSyncedOrderReference;
}

export function buildInitialShipment(
  initialShipment?: Waybill,
  orderContext?: Order,
  syncReferenceWithOrder = false
): Waybill {
  const base = initialShipment
    ? {
        ...defaultNewShipment(),
        ...initialShipment,
        containers:
          (initialShipment?.containers?.length ?? 0) > 0 ? initialShipment.containers : [{}],
      }
    : defaultNewShipment();

  if (
    syncReferenceWithOrder &&
    orderContext?.reference &&
    shouldSyncShipmentReferenceWithOrder(base.containerId)
  ) {
    base.containerId = orderContext.reference;
  }

  if (orderContext && (!base.jobs || base.jobs.length === 0)) {
    base.jobs = [buildJobFromOrder(orderContext)];
  }

  return base as Waybill;
}

interface ShipmentProviderProps {
  children: React.ReactNode;
  initialShipment?: Waybill;
  minContainerSequence?: number;
  orderContext?: Order;
  syncReferenceWithOrder?: boolean;
}

const ShipmentProvider = ({
  children,
  initialShipment,
  minContainerSequence,
  orderContext,
  syncReferenceWithOrder = false,
}: ShipmentProviderProps) => {
  const { currentUser, hasUnsavedChanges, setHasUnsavedChanges } = useAppState();
  const syncedOrderReferenceRef = useRef<string | undefined>(
    syncReferenceWithOrder ? orderContext?.reference : undefined
  );

  const [shipment, setShipment] = useState<Waybill>(() =>
    buildInitialShipment(initialShipment, orderContext, syncReferenceWithOrder)
  );
  const [commodities, setCommodities] = useState<Commodity[]>([]);
  const [containerSizes, setContainerSizes] = useState<ContainerSize[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);

  useEffect(() => {
    if (!syncReferenceWithOrder || !orderContext?.reference) return;

    setShipment((currentShipment) => {
      const shouldSync = shouldSyncShipmentReferenceWithOrder(
        currentShipment?.containerId,
        syncedOrderReferenceRef.current
      );

      if (!shouldSync) return currentShipment;

      syncedOrderReferenceRef.current = orderContext.reference;

      if (currentShipment?.containerId === orderContext.reference) return currentShipment;

      return {
        ...currentShipment,
        containerId: orderContext.reference,
      };
    });
  }, [orderContext?.reference, syncReferenceWithOrder]);

  const Resources: Request = useMemo(
    () => [
      [
        setCommodities,
        WaybillsService.getCommodities,
        { sortBy: JSON.stringify([["label", "ASC"]]) },
      ],
      [setContainerSizes, WaybillsService.getContainerSizes, { sortBy: JSON.stringify(["id"]) }],
      [setMilestones, WaybillsService.getMilestoneDates, { companyId: currentUser?.companyId }],
      [setAgents, AgentService.getAll, { createdCompanyId: currentUser?.companyId }],
      [setDocuments, DocumentService.getAll, { companyId: "", status: Status.ACTIVE }],
    ],
    []
  );

  const requestResults = useQueries({
    queries: Resources.map(([setState, execute, args, enabled = true], index) => ({
      queryKey: [index],
      queryFn: () =>
        execute({
          ...args,
        }),
      enabled,
      refetchOnWindowFocus: false,
      onSuccess: (response: AxiosResponse<(Commodity | ContainerSize | Milestone)[]>) => {
        if (response && response.data && response.data instanceof Array) setState(response.data);
      },
    })),
  });

  const isLoadingQueries = requestResults.some((result) => result.isFetching);

  return (
    <ShipmentsContext.Provider
      value={{
        shipment,
        agents,
        commodities,
        documents,
        containerSizes,
        milestones,
        isLoading: isLoadingQueries,
        setShipment: (shipment, noChanges) => {
          if (!noChanges) setHasUnsavedChanges(true);
          setShipment(shipment);
        },
        hasUnsavedChanges,
        setHasUnsavedChanges,
        minContainerSequence,
        syncReferenceWithOrder,
      }}
    >
      {children}
    </ShipmentsContext.Provider>
  );
};

export { ShipmentProvider, ShipmentsContext };
